/**
 * JoinEventModal — confirmation popup shown when a logged-in non-subscriber
 * taps either the cover "Join" button or the right-panel "Хотите Обсудить?"
 * CTA on an event detail page. Confirm wires through to `subscribeToMaster`,
 * which on the backend also adds the caller to the master's circle Stream
 * channel (one round-trip unlocks both the "Joined" indicator on the event
 * card and the comment input under the thread).
 *
 * Stateless — the caller drives `visible` and supplies the async `onConfirm`.
 * While the promise is pending we show a spinner inside the primary button.
 */

import React, { useState, useCallback } from 'react';
import { Modal, View, Text, TouchableOpacity, ActivityIndicator, StyleSheet } from 'react-native';
import { Typography } from '../ui';
import { useTheme } from '../../context/ThemeContext';

export type JoinEventModalProps = {
  visible: boolean;
  /** Master display name — falls back to a generic title when absent. */
  masterName?: string | null;
  /** Returns a promise so the modal can show pending state until subscribe finishes. */
  onConfirm: () => Promise<void>;
  onCancel: () => void;
};

export const JoinEventModal: React.FC<JoinEventModalProps> = ({
  visible,
  masterName,
  onConfirm,
  onCancel,
}) => {
  const { isDark } = useTheme();
  const [busy, setBusy] = useState(false);

  const handleConfirm = useCallback(async () => {
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
    }
  }, [onConfirm]);

  const mutedColor = isDark ? 'rgba(255,255,255,0.6)' : 'rgba(15,20,25,0.55)';
  const borderColor = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(15,20,25,0.1)';

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onCancel}>
      <View style={styles.backdrop}>
        <View style={[styles.card, { backgroundColor: isDark ? '#1f1b18' : '#fff' }]}>
          <Typography variant="h3" style={{ color: isDark ? '#fff' : '#0F1419', marginBottom: 8 }}>
            Присоединиться{masterName ? ` к ${masterName}` : ''}?
          </Typography>
          <Text
            style={{
              color: mutedColor,
              fontSize: 14,
              lineHeight: 20,
              marginBottom: 20,
            }}
          >
            Вы подпишетесь на мастера и сможете участвовать в обсуждении.
          </Text>
          <View style={{ flexDirection: 'row', gap: 12 }}>
            <TouchableOpacity
              style={[styles.btnSecondary, { borderColor }]}
              onPress={onCancel}
              disabled={busy}
            >
              <Text style={{ color: mutedColor, fontWeight: '600' }}>Отмена</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.btnPrimary} onPress={handleConfirm} disabled={busy}>
              {busy ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                <Text style={{ color: '#fff', fontWeight: '700' }}>Присоединиться</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.55)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  card: { width: '100%', maxWidth: 420, borderRadius: 16, padding: 24 },
  btnSecondary: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 12,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  btnPrimary: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 12,
    backgroundColor: '#E98245',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default JoinEventModal;
